import { useState } from "react";

interface AiAction {
  id: string;
  actionType: string;
  triggerSource: string;
  confidenceScore: number;
  approvalStatus: string;
  resultSummary: string;
  createdAt: string;
  reasoning?: { factors: string[]; confidence: number; alternatives: string[] };
  inputHash?: string;
  outputHash?: string;
  approvedBy?: string;
}

/** F.18: AI Accountability Viewer — legal-grade audit trail */
export default function AdminAiAccountabilityPage() {
  const [actions] = useState<AiAction[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");

  const handleExportPdf = () => {
    // TODO: Wire to PDF generation for compliance reporting
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">AI Actions</h1>
          <p className="text-muted-foreground">Legal-grade audit trail of all AI-initiated actions (EU AI Act Art. 13/14)</p>
        </div>
        <button onClick={handleExportPdf}
          className="rounded-md border border-[var(--color-border)] px-4 py-2 text-sm text-muted-foreground hover:bg-[var(--color-surface)]">
          Export PDF
        </button>
      </div>

      <div className="flex gap-3">
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-white">
          <option value="all">All statuses</option>
          <option value="auto_approved">Auto-approved</option>
          <option value="human_approved">Human-approved</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
          <option value="executed_autonomous">Autonomous</option>
        </select>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-white">
          <option value="all">All action types</option>
          <option value="send_email">Send email</option>
          <option value="create_booking">Create booking</option>
          <option value="update_contact">Update contact</option>
          <option value="create_deal">Create deal</option>
        </select>
      </div>

      {actions.length === 0 ? (
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-12 text-center">
          <span className="text-5xl">🤖</span>
          <p className="mt-4 font-medium text-white">No AI actions recorded yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Actions will appear here as the AI agent processes tasks. Uses ai_accountability_log table (Layer 0.8).
          </p>
        </div>
      ) : (
        <div className="rounded-md border border-[var(--color-border)]">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-[var(--color-background)]">
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Action</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Source</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Confidence</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Result</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Time</th>
              </tr>
            </thead>
            <tbody>
              {actions.map((action) => (
                <>
                  <tr key={action.id} onClick={() => setExpandedId(expandedId === action.id ? null : action.id)}
                    className="border-b border-[var(--color-border)] hover:bg-[var(--color-surface)]/50 cursor-pointer">
                    <td className="px-4 py-3 text-sm text-white font-mono">{action.actionType}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{action.triggerSource}</td>
                    <td className="px-4 py-3">
                      <span className={`text-sm ${action.confidenceScore >= 70 ? "text-[var(--color-success)]" : action.confidenceScore >= 40 ? "text-yellow-400" : "text-[var(--color-destructive)]/80"}`}>
                        {action.confidenceScore}%
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs ${
                        action.approvalStatus === "human_approved" ? "bg-[var(--color-success)] text-[var(--color-success)]/70" :
                        action.approvalStatus === "auto_approved" ? "bg-[var(--color-primary)]/90 text-[var(--color-primary)]/50" :
                        action.approvalStatus === "rejected" ? "bg-[var(--color-destructive)] text-[var(--color-destructive)]/70" :
                        "bg-slate-700 text-muted-foreground"
                      }`}>
                        {action.approvalStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground max-w-xs truncate">{action.resultSummary}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{action.createdAt}</td>
                  </tr>
                  {expandedId === action.id && (
                    <tr key={`${action.id}-detail`}>
                      <td colSpan={6} className="bg-[var(--color-background)] px-4 py-4">
                        <div className="space-y-3 text-sm">
                          {action.reasoning && (
                            <div>
                              <p className="text-xs font-medium text-muted-foreground">Reasoning</p>
                              <ul className="mt-1 list-disc pl-4 text-muted-foreground">
                                {action.reasoning.factors.map((f, i) => <li key={i}>{f}</li>)}
                              </ul>
                            </div>
                          )}
                          <div className="flex gap-8">
                            {action.inputHash && (
                              <div>
                                <p className="text-xs text-muted-foreground">Input hash</p>
                                <p className="font-mono text-xs text-muted-foreground">{action.inputHash}</p>
                              </div>
                            )}
                            {action.outputHash && (
                              <div>
                                <p className="text-xs text-muted-foreground">Output hash</p>
                                <p className="font-mono text-xs text-muted-foreground">{action.outputHash}</p>
                              </div>
                            )}
                          </div>
                          {action.approvedBy && (
                            <p className="text-xs text-muted-foreground">Approved by: {action.approvedBy}</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
